import { useState, useEffect, useCallback } from "react";
import DashboardLayout from "../../../layouts/DashboardLayout";
import api from "../../../api";
import { useNavigate, Link, useSearchParams, useLocation } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import useFeatures from "../../../hooks/useFeatures";
import { Search, User, Phone, Calendar, Clock, ChevronLeft, Stethoscope, Video, MapPin, CheckCircle2, Filter, IndianRupee, Wallet } from "lucide-react";


export default function AppointmentNew() {
  const [searchParams] = useSearchParams();
  const initialDate = searchParams.get('date') || "";
  const initialTime = searchParams.get('time') || "";
  const [searchType, setSearchType] = useState("phone"); // phone | name
  const [searchQuery, setSearchQuery] = useState("");
  const [patients, setPatients] = useState([]); // search results
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [loading, setLoading] = useState(false);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [error, setError] = useState("");
  const [doctors, setdoctors] = useState([]);
  const [specializations, setSpecializations] = useState([]);
  const [specializationFilter, setSpecializationFilter] = useState("");
  const [appointmentData, setAppointmentData] = useState({
    date: initialDate,
    time: initialTime,
    doctor: "",
    appointmentType: "Online",
    audioOnly: false,
    repeat: "",
    repeatCount: 6,
    fee: "",
    collectionMethod: "Default"
  });
  const navigate = useNavigate();
  const location = useLocation();
  const features = useFeatures();
  const appointmentTypes = ["Clinic", "Online"];
  const collectionMethods = [
    { value: "Default", label: "Default (Org Setting)" },
    { value: "Online", label: "Online" },
    { value: "At Clinic", label: "At Clinic" },
  ];

  // Load doctors and auto-select if logged-in user is a doctor
  useEffect(() => {
    const fetchSlots = async () => {
      if (appointmentData.doctor && appointmentData.date) {
        setSlotsLoading(true);
        try {
          const res = await api.fetchDoctorSlots(appointmentData.doctor, appointmentData.date);
          const slots = res.data.slots || [];
          setAvailableSlots(slots);
          // Drop a selection that's no longer bookable (passed or taken).
          setAppointmentData(prev => {
            const picked = slots.find(sl => sl.start_time === prev.time);
            return picked && !picked.is_booked && !picked.is_past ? prev : { ...prev, time: "" };
          });
        } catch (error) {
          console.error("Failed to fetch slots", error);
          setAvailableSlots([]);
        } finally {
          setSlotsLoading(false);
        }
      } else {
        setAvailableSlots([]);
      }
    };
    fetchSlots();
  }, [appointmentData.doctor, appointmentData.date]);

  // Load specialization list for the doctor filter
  useEffect(() => {
    api.fetchSpecializations()
      .then((res) => {
        const list = res.data?.data;
        if (Array.isArray(list)) setSpecializations(list);
      })
      .catch((err) => console.error("Failed to fetch specializations:", err));
  }, []);

  const loaddoctors = useCallback(async (loggedInUserId = null) => {
    try {
      const res = await api.fetchDoctors();

      // If logged-in user is a doctor, only show their name in the dropdown
      if (loggedInUserId && ((res.data.data ? res.data.data : res.data) && (res.data.data ? res.data.data : res.data).length > 0)) {
        // Try to find doctor by doctor_id (which is actually user_id)
        // Handle both string and number comparisons
        const userIdNum = typeof loggedInUserId === 'string' ? parseInt(loggedInUserId) : loggedInUserId;
        const currentDoctor = (res.data.data ? res.data.data : res.data).find(doc => {
          const docIdNum = typeof doc.doctor_id === 'string' ? parseInt(doc.doctor_id) : doc.doctor_id;
          return docIdNum === userIdNum;
        });

        if (currentDoctor) {
          // Only show the logged-in doctor
          setdoctors([currentDoctor]);
          // Auto-select the doctor
          const doctorId = typeof currentDoctor.doctor_id === 'string'
            ? currentDoctor.doctor_id
            : currentDoctor.doctor_id.toString();
          setAppointmentData(prev => ({
            ...prev,
            doctor: doctorId
          }));
        } else {
          // Doctor not found in list, set empty
          setdoctors([]);
        }
      } else {
        // For non-doctor users, show every active doctor - a deactivated one
        // can't be booked (the slot lookup answers "Doctor not found").
        setdoctors(((res.data.data ? res.data.data : res.data) || []).filter((doc) => doc.is_active !== false));
      }
    } catch (err) {
      setError("Failed to load doctors: " + err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Get logged-in user info and auto-select doctor if user is a doctor
  useEffect(() => {
    const storedUser = localStorage.getItem("auth");
    if (storedUser) {
      try {
        const authData = JSON.parse(storedUser);
        if (authData.token) {
          const decoded = jwtDecode(authData.token);
          const userRole = decoded.sub?.role?.toLowerCase();
          const userId = decoded.sub?.user_id;

          // If user is a doctor, load doctors and auto-select
          if (userRole === "doctor" && userId) {
            loaddoctors(userId);
          }
        }
      } catch (err) {
        console.error("Error decoding token:", err);
      }
    }
  }, [loaddoctors]);

  // ✅ Search patients via API
  const performSearch = useCallback(async (query) => {
    if (!query || !query.trim()) return;
    try {
      setLoading(true);
      setError("");
      const res = await api.searchPatient(query);  // ✅ call API directly
      const results = (res.data.data ? res.data.data : res.data) || [];
      setPatients(results);
      setSelectedPatient(null);
      if (results.length === 0) { setError("No record found.") } else {
        // Get logged-in user ID for auto-selecting doctor
        const storedUser = localStorage.getItem("auth");
        let loggedInUserId = null;
        if (storedUser) {
          try {
            const authData = JSON.parse(storedUser);
            if (authData.token) {
              const decoded = jwtDecode(authData.token);
              const userRole = decoded.sub?.role?.toLowerCase();
              if (userRole === "doctor") {
                loggedInUserId = decoded.sub?.user_id;
              }
            }
          } catch (err) {
            console.error("Error decoding token:", err);
          }
        }
        loaddoctors(loggedInUserId);
      }
    } catch (err) {
      alert("Failed to search patient. Try after sometime.");
    } finally {
      setLoading(false);
    }
  }, [loaddoctors]);

  const handleSearch = () => {
    if (!searchQuery || !searchQuery.trim()) {
      alert("Please enter search query");
      return;
    }
    performSearch(searchQuery);
  };

  // If we were sent back here after creating a new patient (see the
  // "no record found" panel below → PatientNew), re-run the search for
  // that phone number so the newly created patient is selected automatically.
  useEffect(() => {
    if (location.state?.newPatientPhone) {
      setSearchType("phone");
      setSearchQuery(location.state.newPatientPhone);
      performSearch(location.state.newPatientPhone);
      // Clear the navigation state so a page refresh doesn't re-trigger this.
      navigate(location.pathname, { replace: true });
    }
  }, []);


  const handleAppointmentChange = (e) => {
    const { name, value } = e.target;
    setAppointmentData({
      ...appointmentData,
      [name]: value,
    });
  };

  // ✅ Create appointment via API
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedPatient) {
      alert("Please select a patient first!");
      return;
    }

    try {
      const payload = {
        patient_id: selectedPatient.id,
        date: appointmentData.date,
        time: appointmentData.time,
        doctor: appointmentData.doctor,
        appointmentType: appointmentData.appointmentType,
        audio_only: appointmentData.appointmentType === "Online" && appointmentData.audioOnly && features.has("audio_sessions"),
        fee: appointmentData.fee === "" ? 0 : Number(appointmentData.fee),
        collection_method: appointmentData.collectionMethod,
        ...(appointmentData.repeat && features.has("recurring")
          ? { repeat: { frequency: appointmentData.repeat, count: Number(appointmentData.repeatCount) } } : {}),
      };

      const res = await api.createAppointment(payload);
      if (res.status != 201) throw new Error(res.data?.error || "Failed to create appointment");

      if (res.data?.series_id) {
        const skipped = res.data.skipped || [];
        alert(`${res.data.created.length} appointments created.` + (skipped.length
          ? `\n\nSkipped ${skipped.length}:\n` + skipped.map((s) => `${new Date(`${s.date}T00:00:00`).toLocaleDateString("en-IN", { day: "numeric", month: "short" })} - ${s.reason}`).join("\n")
          : ""));
      } else {
        alert("Appointment created successfully!");
      }
      navigate("/appointment");
    } catch (err) {
      console.error("Appointment creation failed:", err);
      const skipped = err?.response?.data?.skipped;
      alert((err?.response?.data?.error || err.message) + (skipped?.length ? "\n" + skipped.map((s) => `${s.date} - ${s.reason}`).join("\n") : ""));
    }
  };


  return (
    <DashboardLayout>
      {/* Back Button */}
      <div className="mb-4 max-w-4xl mx-auto">
        <Link
          to="/appointment"
          className="inline-flex items-center gap-2 text-gray-500 hover:text-gray-800 transition-colors"
        >
          <ChevronLeft className="w-5 h-5" />
          <span className="font-medium">Back to Appointments</span>
        </Link>
      </div>

      <div className="max-w-4xl mx-auto animate-fade-in-up">
        <div className="bg-white shadow-sm border border-secondary-100 rounded-2xl overflow-hidden">
          <div className="px-6 py-5 border-b border-secondary-100 flex items-center gap-3">
            <div className="w-9 h-9 bg-primary-100 rounded-xl flex items-center justify-center">
              <Calendar className="w-5 h-5 text-primary-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-secondary-900">Book New Appointment</h2>
              <p className="text-xs text-secondary-400">Search for a patient and schedule their consultation</p>
            </div>
          </div>

          <div className="p-6 sm:p-8 space-y-8">
            {/* Search Section */}
            <div>
              <label className="block text-sm font-semibold text-secondary-700 mb-2">Find Patient</label>
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative w-full sm:w-1/3">
                  <select
                    value={searchType}
                    onChange={(e) => setSearchType(e.target.value)}
                    className="w-full appearance-none bg-secondary-50 border border-secondary-200 text-secondary-700 py-3 px-4 pr-8 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent font-medium"
                  >
                    <option value="name">Patient Name</option>
                    <option value="phone">Phone Number</option>

                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-secondary-500">
                    <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" /></svg>
                  </div>
                </div>

                <div className="relative flex-1">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    {searchType === "phone" ? (
                      <Phone className="h-5 w-5 text-secondary-400" />
                    ) : (
                      <User className="h-5 w-5 text-secondary-400" />
                    )}
                  </div>
                  <input
                    type={searchType === "phone" ? "number" : "text"}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && searchQuery && !loading) {
                        e.preventDefault();
                        handleSearch();
                      }
                    }}
                    placeholder={searchType === "phone" ? "Enter phone number..." : "Enter patient name..."}
                    className="w-full pl-10 pr-4 py-3 bg-secondary-50 border border-secondary-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                  />
                </div>

                <button
                  type="button"
                  onClick={handleSearch}
                  disabled={loading || !searchQuery}
                  className="flex items-center justify-center bg-primary-600 text-white px-6 py-3 rounded-xl hover:bg-primary-700 transition-colors font-medium shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    <>
                      <Search className="w-4 h-4 mr-2" /> Search
                    </>
                  )}
                </button>
              </div>
              {error && error !== "No record found." && (
                <p className="text-red-500 mt-2 text-sm flex items-center"><CheckCircle2 className="w-4 h-4 mr-1" /> {error}</p>
              )}

              {error === "No record found." && (
                <p className="mt-2 text-sm text-secondary-500">
                  No patient found{searchType === "phone" && searchQuery ? ` for "${searchQuery}"` : ""}.{" "}
                  <button
                    type="button"
                    onClick={() => navigate("/patient-new", {
                      state: {
                        prefillContact: searchType === "phone" ? searchQuery.trim() : "",
                        returnTo: "/appointment-new"
                      }
                    })}
                    className="text-primary-600 hover:text-primary-700 font-semibold underline underline-offset-2"
                  >
                    Create new patient &rarr;
                  </button>
                </p>
              )}
            </div>

            {/* Search Results */}
            {patients.length > 1 && (
              <div className="bg-secondary-50 rounded-xl p-4 border border-secondary-200 animate-fade-in-up">
                <h4 className="font-semibold text-secondary-800 mb-3 text-sm uppercase tracking-wider">Select a Patient</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {patients.map((p) => (
                    <label key={p.id} className={`flex items-start p-3 rounded-lg border cursor-pointer transition-all ${selectedPatient?.id === p.id ? 'bg-primary-50 border-primary-300 ring-1 ring-primary-500' : 'bg-white border-secondary-200 hover:border-primary-300'}`}>
                      <input
                        type="radio"
                        name="patient"
                        value={p.id}
                        checked={selectedPatient?.id === p.id}
                        onChange={() => setSelectedPatient(p)}
                        className="mt-1 mr-3 text-primary-600 focus:ring-primary-500 w-4 h-4"
                      />
                      <div>
                        <p className="font-medium text-secondary-900">{p.name}</p>
                        <p className="text-sm text-secondary-500">
                          {[
                            p.phone || "No phone on file",
                            p.dob ? `${Math.floor((Date.now() - new Date(`${p.dob}T00:00:00`)) / 31557600000)} yrs` : null,
                            p.gender,
                          ].filter(Boolean).join(" · ")}
                        </p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Hidden auto-select for single patient */}
            {patients.length === 1 && !selectedPatient && setSelectedPatient(patients[0])}

            {/* Existing Open Appointments Table */}
            {selectedPatient?.has_open_appointment && selectedPatient.open_appointment_details?.length > 0 && (
              <div className="pt-6 border-t border-secondary-100 animate-fade-in-up">
                <div className="bg-yellow-50 rounded-xl border border-yellow-200 overflow-hidden mb-2">
                  <div className="bg-yellow-100 px-4 py-3 border-b border-yellow-200 flex items-center">
                    <svg className="h-5 w-5 text-yellow-600 mr-2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    <h3 className="text-sm font-bold text-yellow-800 uppercase tracking-wider">Warning: Open Appointments Found</h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left">
                      <thead className="bg-yellow-50/50 text-yellow-800 border-b border-yellow-200">
                        <tr>
                          <th className="px-4 py-3 font-semibold">Doctor</th>
                          <th className="px-4 py-3 font-semibold">Date</th>
                          <th className="px-4 py-3 font-semibold">Time</th>
                          <th className="px-4 py-3 font-semibold text-right">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-yellow-200/50">
                        {selectedPatient.open_appointment_details.map((appt) => (
                          <tr key={appt.id} className="hover:bg-yellow-100/30 transition-colors">
                            <td className="px-4 py-3 font-medium text-yellow-900">Dr. {appt.doctor_name}</td>
                            <td className="px-4 py-3 text-yellow-800">{appt.date}</td>
                            <td className="px-4 py-3 text-yellow-800">{appt.time?.substring(0, 5)}</td>
                            <td className="px-4 py-3 text-right">
                              <button
                                type="button"
                                onClick={() => navigate('/appointment-edit', { state: { ...appt, patient_name: selectedPatient.name, patient_phone: selectedPatient.phone, id: appt.id } })}
                                className="inline-flex items-center text-xs font-semibold bg-yellow-200 text-yellow-900 px-3 py-1.5 rounded-lg hover:bg-yellow-300 transition-colors"
                              >
                                Edit
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Main Content Area: split into two columns if patient selected */}
            {selectedPatient && (
              <div className={`grid grid-cols-1 md:grid-cols-3 gap-8 pt-6 ${!selectedPatient?.has_open_appointment ? 'border-t border-secondary-100' : ''} animate-fade-in-up`}>

                {/* Left Column: Patient Summary */}
                <div className="md:col-span-1 space-y-6">
                  <div className="bg-gradient-to-br from-primary-50 to-white p-5 rounded-2xl border border-primary-100 shadow-sm">
                    <div className="w-12 h-12 bg-primary-100 text-primary-600 rounded-full flex items-center justify-center mb-4">
                      <User className="w-6 h-6" />
                    </div>
                    <h3 className="font-bold text-lg text-secondary-900 mb-1">{selectedPatient.name}</h3>
                    <p className="text-secondary-500 text-sm mb-4">{selectedPatient.phone}</p>

                    <div className="space-y-3 pt-4 border-t border-primary-100 text-sm">
                      <div className="flex justify-between">
                        <span className="text-secondary-500">DOB</span>
                        <span className="font-medium text-secondary-900">{selectedPatient.dob || 'N/A'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-secondary-500">Gender</span>
                        <span className="font-medium text-secondary-900">{selectedPatient.gender || 'N/A'}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right Column: Scheduling Form */}
                <div className="md:col-span-2">
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <h3 className="font-semibold text-lg text-secondary-900 mb-4 flex items-center">
                      <Clock className="w-5 h-5 mr-2 text-secondary-400" />
                      Schedule Details
                    </h3>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      {doctors.length > 1 && (
                        <div className="sm:col-span-2">
                          <label className="block text-sm font-medium text-secondary-700 mb-1">Filter by Specialization</label>
                          <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                              <Filter className="h-5 w-5 text-secondary-400" />
                            </div>
                            <select
                              value={specializationFilter}
                              onChange={(e) => {
                                setSpecializationFilter(e.target.value);
                                setAppointmentData(prev => ({ ...prev, doctor: "", time: "" })); // reset doctor/time when filter changes
                              }}
                              className="w-full pl-10 pr-4 py-2.5 bg-secondary-50 border border-secondary-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all appearance-none"
                            >
                              <option value="">All Specializations</option>
                              {specializations.map((s) => (
                                <option key={s.id || s.name} value={s.name}>{s.name}</option>
                              ))}
                            </select>
                            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-secondary-500">
                              <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" /></svg>
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="sm:col-span-2">
                        <label className="block text-sm font-medium text-secondary-700 mb-1">Doctor</label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <Stethoscope className="h-5 w-5 text-secondary-400" />
                          </div>
                          <select
                            name="doctor"
                            value={appointmentData.doctor}
                            onChange={(e) => {
                                handleAppointmentChange(e);
                                setAppointmentData(prev => ({...prev, time: ""})); // reset time when doctor changes
                            }}
                            className="w-full pl-10 pr-4 py-2.5 bg-secondary-50 border border-secondary-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all appearance-none"
                            required
                            disabled={doctors.length === 1 && appointmentData.doctor !== ""}
                          >
                            <option value="">
                              {doctors.filter((doc) => !specializationFilter || doc.specialized === specializationFilter).length === 0
                                ? "No doctors match this specialization"
                                : "Select a Doctor..."}
                            </option>
                            {doctors
                              .filter((doc) => !specializationFilter || doc.specialized === specializationFilter)
                              .map((doc) => (
                                <option key={doc.doctor_id} value={doc.doctor_id}>
                                  {doc.name} ({doc.phone_number}){doc.specialized ? ` – ${doc.specialized}` : ""}
                                </option>
                              ))}
                          </select>
                          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-secondary-500">
                            <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" /></svg>
                          </div>
                        </div>
                      </div>

                      <div className="sm:col-span-2">
                        <label className="block text-sm font-medium text-secondary-700 mb-1">Date</label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <Calendar className="h-5 w-5 text-secondary-400" />
                          </div>
                          <input
                            type="date"
                            name="date"
                            min={new Date().toLocaleDateString('en-CA')}
                            value={appointmentData.date}
                            onChange={(e) => {
                                handleAppointmentChange(e);
                                setAppointmentData(prev => ({...prev, time: ""})); // reset time when date changes
                            }}
                            min={new Date().toISOString().split('T')[0]}
                            className="w-full pl-10 pr-4 py-2.5 bg-secondary-50 border border-secondary-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all"
                            required
                          />
                        </div>
                      </div>

                      <div className="sm:col-span-2">
                        <label className="block text-sm font-medium text-secondary-700 mb-2 flex items-center justify-between">
                          <span>Time Slots</span>
                          {slotsLoading && <div className="w-4 h-4 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>}
                        </label>
                        {!appointmentData.doctor || !appointmentData.date ? (
                            <div className="text-sm text-secondary-500 p-4 border border-dashed rounded-xl text-center bg-secondary-50">
                                Please select a doctor and date to view available slots.
                            </div>
                        ) : availableSlots.length === 0 && !slotsLoading ? (
                            <div className="text-sm text-secondary-500 p-4 border border-dashed rounded-xl text-center bg-secondary-50">
                                No slots available on this date.
                            </div>
                        ) : (
                            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                              {availableSlots.map((slot) => {
                                const isSelected = appointmentData.time === slot.start_time;
                                const unavailable = slot.is_booked || slot.is_past;
                                return (
                                  <button
                                    key={slot.id}
                                    type="button"
                                    disabled={unavailable}
                                    title={slot.is_past ? 'This time has passed' : slot.is_booked ? 'Already booked' : undefined}
                                    onClick={() => setAppointmentData(prev => ({...prev, time: slot.start_time, fee: slot.fee != null ? String(slot.fee) : prev.fee}))}
                                    className={`py-2 px-1 text-sm font-medium rounded-lg border transition-all ${
                                      unavailable
                                        ? `bg-secondary-100 text-secondary-400 border-secondary-200 cursor-not-allowed opacity-60${slot.is_booked ? ' line-through' : ''}`
                                        : isSelected
                                          ? 'bg-primary-600 text-white border-primary-600 shadow-sm'
                                          : 'bg-white text-secondary-700 border-secondary-200 hover:border-primary-500 hover:text-primary-600'
                                    }`}
                                  >
                                    {slot.start_time}
                                  </button>
                                );
                              })}
                            </div>
                        )}
                        <input type="text" className="sr-only" required value={appointmentData.time} onChange={() => {}} tabIndex={-1} />
                      </div>

                      <div className="sm:col-span-2">
                        <label className="block text-sm font-medium text-secondary-700 mb-2">Appointment Type</label>
                        <div className="grid grid-cols-2 gap-3">
                          {appointmentTypes.map((type) => (
                            <label key={type} className={`flex items-center justify-center py-3 px-4 rounded-xl border cursor-pointer transition-all ${appointmentData.appointmentType === type ? 'bg-primary-50 border-primary-400 text-primary-700 ring-1 ring-primary-500' : 'bg-white border-secondary-200 hover:border-primary-300 text-secondary-700'}`}>
                              <input
                                type="radio"
                                name="appointmentType"
                                value={type}
                                checked={appointmentData.appointmentType === type}
                                onChange={handleAppointmentChange}
                                className="sr-only"
                              />
                              {type === 'Online' ? <Video className="w-5 h-5 mr-2" /> : <MapPin className="w-5 h-5 mr-2" />}
                              <span className="font-semibold">{type}</span>
                            </label>
                          ))}
                        </div>
                        {appointmentData.appointmentType === "Online" && features.has("audio_sessions") && (
                          <label className="normal-case font-sans tracking-normal mt-3 flex items-start gap-2 text-sm text-secondary-700 cursor-pointer">
                            <input type="checkbox" checked={appointmentData.audioOnly}
                              onChange={(e) => setAppointmentData((d) => ({ ...d, audioOnly: e.target.checked }))}
                              className="mt-0.5 h-4 w-4 rounded border-secondary-300 text-primary-600" />
                            <span><span className="font-semibold">Voice call (no camera)</span> - the call starts with cameras off, for patients who prefer not to be seen.</span>
                          </label>
                        )}
                      </div>

                      {features.has("recurring") && (
                        <div className="sm:col-span-2">
                          <label htmlFor="repeat" className="block text-sm font-medium text-secondary-700 mb-1">Repeats</label>
                          <div className="flex flex-wrap items-center gap-2">
                            <select id="repeat" value={appointmentData.repeat}
                              onChange={(e) => setAppointmentData((d) => ({ ...d, repeat: e.target.value }))}
                              className="rounded-xl border border-secondary-200 bg-white px-3 py-2.5 text-sm">
                              <option value="">Doesn't repeat</option>
                              <option value="weekly">Every week</option>
                              <option value="fortnightly">Every 2 weeks</option>
                              <option value="four_weekly">Every 4 weeks</option>
                            </select>
                            {appointmentData.repeat && (
                              <>
                                <span className="text-sm text-secondary-600">for</span>
                                <input type="number" min={2} max={26} value={appointmentData.repeatCount} aria-label="Number of visits"
                                  onWheel={(e) => e.currentTarget.blur()}
                                  onChange={(e) => setAppointmentData((d) => ({ ...d, repeatCount: e.target.value }))}
                                  className="w-20 rounded-xl border border-secondary-200 bg-white px-3 py-2.5 text-sm" />
                                <span className="text-sm text-secondary-600">visits</span>
                              </>
                            )}
                          </div>
                          {appointmentData.repeat && (
                            <p className="text-xs text-secondary-500 mt-1">Same day and time each time. Dates when the doctor is already booked or the clinic is closed are skipped, and you'll see which.</p>
                          )}
                        </div>
                      )}

                      <div className="sm:col-span-1">
                        <label className="block text-sm font-medium text-secondary-700 mb-1">Consultation Fee</label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <IndianRupee className="h-5 w-5 text-secondary-400" />
                          </div>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            name="fee"
                            value={appointmentData.fee}
                            onChange={handleAppointmentChange}
                            placeholder="0.00"
                            className="w-full pl-10 pr-4 py-2.5 bg-secondary-50 border border-secondary-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all"
                          />
                        </div>
                        <p className="text-xs text-secondary-400 mt-1">Prefilled from the selected slot - editable.</p>
                      </div>

                      <div className="sm:col-span-1">
                        <label className="block text-sm font-medium text-secondary-700 mb-1">Collection Method</label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <Wallet className="h-5 w-5 text-secondary-400" />
                          </div>
                          <select
                            name="collectionMethod"
                            value={appointmentData.collectionMethod}
                            onChange={handleAppointmentChange}
                            className="w-full pl-10 pr-4 py-2.5 bg-secondary-50 border border-secondary-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all appearance-none"
                          >
                            {collectionMethods.map((m) => (
                              <option key={m.value} value={m.value}>{m.label}</option>
                            ))}
                          </select>
                          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-secondary-500">
                            <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" /></svg>
                          </div>
                        </div>
                      </div>

                      <div className="sm:col-span-2 flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2.5">
                        <CheckCircle2 className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                        <p className="text-xs text-amber-800">
                          The patient will need to confirm this appointment from their app before it's finalized.
                          {appointmentData.collectionMethod !== "At Clinic" && " If the collection method resolves to Online, confirming means completing payment."}
                        </p>
                      </div>
                    </div>

                    <div className="pt-6 mt-6 border-t border-secondary-100 flex justify-end">
                      <button
                        type="submit"
                        className="flex items-center bg-primary-600 text-white px-8 py-3 rounded-xl hover:bg-primary-700 transition-colors shadow-sm font-semibold text-lg"
                      >
                        Confirm Booking
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
